import { useState, type FormEvent } from 'react';
import {
  TICKET_SEVERITIES, TICKET_TRANSITIONS, TRANSITIONS, canTransition,
  type LifecycleState, type TicketSeverity, type TicketState,
} from '@gc/domain';
import type { ApiClient, GadgetRow, RepairRow } from '@gc/api-client';
import { useAction, useResource } from '@gc/ui-state';
import { repairLabel, rupees, stateLabel, words } from './labels.js';

export type RepairsApi = {
  lifecycle: Pick<ApiClient['lifecycle'], 'tickets' | 'advance'>;
  repairs: Pick<ApiClient['repairs'], 'create'>;
};

const IN_REPAIR: readonly string[] = ['under_repair', 'awaiting_parts'];
const RETURN_TO = TRANSITIONS.under_repair.filter((s) => s !== 'awaiting_parts');

export function Repairs({ gadget, api, canLog, onChanged }: {
  gadget: GadgetRow;
  api: RepairsApi;
  canLog: boolean;
  onChanged: () => void;
}) {
  const { load, reload } = useResource(() => api.lifecycle.tickets(gadget.id), [gadget.id]);
  const { busy, failure, run } = useAction();
  const [raising, setRaising] = useState(false);
  const [fault, setFault] = useState('');
  const [severity, setSeverity] = useState<TicketSeverity>('medium');
  const [advancing, setAdvancing] = useState<RepairRow | null>(null);
  const [to, setTo] = useState<TicketState | ''>('');
  const [reason, setReason] = useState('');
  const [cost, setCost] = useState('');
  const [resolution, setResolution] = useState('');
  const [returnTo, setReturnTo] = useState<LifecycleState>(RETURN_TO[0] ?? 'in_service');

  const canRaise = canLog && (canTransition(gadget.status, 'under_repair') || IN_REPAIR.includes(gadget.status));
  const closing = to === 'completed' || to === 'cancelled';

  const done = (ok: boolean) => {
    if (!ok) return;
    setRaising(false);
    setAdvancing(null);
    setFault('');
    reload();
    onChanged();
  };

  const raise = (e: FormEvent) => {
    e.preventDefault();
    void run(() => api.repairs.create({ gadgetId: gadget.id, fault: fault.trim(), severity })).then(done);
  };

  const startAdvance = (t: RepairRow) => {
    setAdvancing(t);
    setTo(TICKET_TRANSITIONS[t.status][0] ?? '');
    setReason('');
    setCost('');
    setResolution('');
  };

  const advance = (e: FormEvent) => {
    e.preventDefault();
    if (!advancing || to === '') return;
    void run(() => api.lifecycle.advance(advancing.id, {
      to,
      reason: reason.trim(),
      ...(to === 'completed' && cost.trim() ? { costMinor: Math.round(Number(cost) * 100) } : {}),
      ...(to === 'completed' && resolution.trim() ? { resolution: resolution.trim() } : {}),
      ...(closing ? { returnGadgetTo: returnTo } : {}),
    })).then(done);
  };

  return (
    <div data-testid="repairs">
      <div className="panel-head">
        <h3>Repairs</h3>
        {canRaise && !raising && <button type="button" onClick={() => setRaising(true)} data-testid="action-raise">Raise a repair</button>}
      </div>
      {failure && <p role="alert" data-testid="repairs-failure">{failure}</p>}

      {raising && (
        <form className="form" onSubmit={raise} data-testid="raise-form">
          <div className="grid">
            <label>Fault<input required minLength={5} value={fault} onChange={(e) => setFault(e.target.value)} data-testid="raise-fault" /></label>
            <label>Severity
              <select value={severity} onChange={(e) => setSeverity(e.target.value as TicketSeverity)} data-testid="raise-severity">
                {TICKET_SEVERITIES.map((s) => <option key={s} value={s}>{words(s)}</option>)}
              </select>
            </label>
          </div>
          <div className="form-actions">
            <button type="submit" className="primary" disabled={busy}>Raise</button>
            <button type="button" onClick={() => setRaising(false)} disabled={busy}>Cancel</button>
          </div>
        </form>
      )}

      {load.kind === 'loading' && <p>Loading repairs…</p>}
      {load.kind === 'error' && <p role="alert" data-testid="repairs-error">Could not load repairs: {load.message}</p>}
      {load.kind === 'ready' && (load.data.items.length === 0
        ? <p data-testid="repairs-empty">No repairs recorded.</p>
        : (
          <div className="scroll-x">
            <table>
              <thead>
                <tr>
                  <th scope="col">ID</th><th scope="col">Fault</th><th scope="col">Severity</th>
                  <th scope="col">Status</th><th scope="col">Cost</th>
                  <th scope="col"><span className="visually-hidden">Actions</span></th>
                </tr>
              </thead>
              <tbody>
                {load.data.items.map((t) => (
                  <tr key={t.id} data-testid={`ticket-${t.shortId}`}>
                    <td><code>{t.shortId}</code></td>
                    <td>{t.fault}</td>
                    <td>{words(t.severity)}</td>
                    <td><span className={`pill ${t.status}`}>{repairLabel(t.status)}</span></td>
                    <td>{rupees(t.costMinor)}</td>
                    <td className="row-action">
                      {canLog && TICKET_TRANSITIONS[t.status].length > 0 && (
                        <button type="button" onClick={() => startAdvance(t)} aria-label={`Advance ${t.shortId}`}>Advance</button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ))}

      {advancing && (
        <form className="form" onSubmit={advance} data-testid="advance-form">
          <h3>Advance {advancing.shortId}</h3>
          <div className="grid">
            <label>Move to
              <select value={to} onChange={(e) => setTo(e.target.value as TicketState)} data-testid="advance-to">
                {TICKET_TRANSITIONS[advancing.status].map((s) => <option key={s} value={s}>{repairLabel(s)}</option>)}
              </select>
            </label>
            <label>Reason<input required minLength={3} value={reason} onChange={(e) => setReason(e.target.value)} data-testid="advance-reason" /></label>
            {to === 'completed' && (
              <>
                <label>Cost (₹)<input type="number" min="0" step="0.01" value={cost} onChange={(e) => setCost(e.target.value)} data-testid="advance-cost" /></label>
                <label>Resolution<input value={resolution} onChange={(e) => setResolution(e.target.value)} /></label>
              </>
            )}
            {closing && (
              <label>Gadget goes to
                <select value={returnTo} onChange={(e) => setReturnTo(e.target.value as LifecycleState)} data-testid="advance-return">
                  {RETURN_TO.map((s) => <option key={s} value={s}>{stateLabel(s)}</option>)}
                </select>
              </label>
            )}
          </div>
          <div className="form-actions">
            <button type="submit" className="primary" disabled={busy}>Save</button>
            <button type="button" onClick={() => setAdvancing(null)} disabled={busy}>Cancel</button>
          </div>
        </form>
      )}
    </div>
  );
}
